import React, { useMemo, useRef } from 'react';
import FormCard from 'pages/NewUniProfile/components/FormCard';
import ProfessionalProfile from 'pages/NewUniProfile/components/ProfessionalProfileStep/components/ProfessionalProfile';
import AdditionalMedia from 'pages/NewUniProfile/components/ProfessionalProfileStep/components/AdditionalMedia';
import Healthcare from 'pages/NewUniProfile/components/BackgroundStep/components/Healthcare';
import Education from 'pages/NewUniProfile/components/BackgroundStep/components/Education';
import Treatment from 'pages/NewUniProfile/components/BackgroundStep/components/Treatment';
import DemographicDetails from 'pages/NewUniProfile/components/BackgroundStep/components/DemographicDetails';
import Recognition from 'pages/NewUniProfile/components/BackgroundStep/components/Recognition';
import MalpracticeInsuranceCertificate from 'pages/NewUniProfile/components/LicenseStep/components/MalpracticeInsuranceCertificate';
import DEALicense from 'pages/NewUniProfile/components/LicenseStep/components/DeaLicense';
import CollaboratingPhysician from 'pages/NewUniProfile/components/LicenseStep/components/CollaboratingPhysician';
import AdditionalLicense from 'pages/NewUniProfile/components/LicenseStep/components/AdditionalLicense';
import BasicInfoCard from '../BasicInfoCard';
import { useBasicInfo } from 'hooks/newUniprofile/useBasicInfo';
import { useLicenseInfo } from 'hooks/newUniprofile/useLicenseInfo';
import { useProfessionalProfile } from 'hooks/newUniprofile/useProfessionalProfile';
import { useBackgroundInfo } from 'hooks/newUniprofile/useBackgroundInfo';
import { useSwitchedProvider } from '../../SwitchedProviderContext';
import s from './s.module.less';

const ProfileTab: React.FC = () => {
    const { scheduleToken } = useSwitchedProvider();
    const [basicInfo, , fetchBasicInfoData] = useBasicInfo(false, scheduleToken);
    const [licenseInfo, , fetchLicenseData] = useLicenseInfo(false, scheduleToken);
    const [professionalProfile, , fetchProfessionalProfileData] = useProfessionalProfile(false, scheduleToken);
    const [background, , , updateBackgroundInfo] = useBackgroundInfo(false, scheduleToken);
    const professionalProfileRef = useRef<any>(null);

    const hasAdditionalMedia = Boolean(
        professionalProfile?.additionalPhotoList?.length ||
        professionalProfile?.videoList?.length ||
        professionalProfile?.foreignLanguage,
    );

    const hasAdditionalLicense =
        !!licenseInfo?.additionalLicensesNCertificates?.controlledSubstanceCertification?.length ||
        !!licenseInfo?.additionalLicensesNCertificates?.continuingMedicalEducationCertificates?.length ||
        !!licenseInfo?.additionalLicensesNCertificates?.autonomy?.url ||
        !!licenseInfo?.additionalLicensesNCertificates?.other?.url;

    const hasContentForBiographic = useMemo(() => {
        return Object.values(background.biographic).some((value) => Boolean(value));
    }, [background]);

    const hasContentForRecognition = useMemo(() => {
        return (
            Boolean(background.awardRecognitionList?.length) ||
            Boolean(background.associationRecognitionList?.length) ||
            Boolean(background.publicationRecognitionList?.length)
        );
    }, [background]);

    return (
        <div className={s.wrap}>
            <BasicInfoCard initValue={basicInfo} onSave={fetchBasicInfoData} />

            <FormCard type="review" ref={professionalProfileRef} title="Professional profile">
                {(isEditing) => (
                    <ProfessionalProfile
                        isEditing={isEditing}
                        initValue={professionalProfile}
                        refetch={fetchProfessionalProfileData}
                        scheduleToken={scheduleToken}
                    />
                )}
            </FormCard>

            <FormCard
                type={hasAdditionalMedia ? 'review' : 'add'}
                title="Additional Media"
                isOptional
                hasContent={hasAdditionalMedia}
            >
                {(isEditing) => (
                    <AdditionalMedia
                        isEditing={isEditing}
                        initValue={professionalProfile}
                        refetch={fetchProfessionalProfileData}
                        scheduleToken={scheduleToken}
                    />
                )}
            </FormCard>

            <FormCard
                type={Boolean(background.providerExperienceList?.length) ? 'review' : 'add'}
                title="Healthcare experience"
                isOptional
                hasContent={Boolean(background.providerExperienceList?.length)}
            >
                {(isEditing) => (
                    <Healthcare
                        isEditing={isEditing}
                        initValue={background.providerExperienceList}
                        updateFn={updateBackgroundInfo}
                    />
                )}
            </FormCard>

            <FormCard
                type={Boolean(background.providerEducationList?.length) ? 'review' : 'add'}
                title="Education"
                isOptional
                hasContent={Boolean(background.providerEducationList?.length)}
            >
                {(isEditing) => (
                    <Education
                        isEditing={isEditing}
                        initValue={background.providerEducationList}
                        updateFn={updateBackgroundInfo}
                    />
                )}
            </FormCard>

            <FormCard
                type={Boolean(background.treatmentPhilosophy) ? 'review' : 'add'}
                title="Treatment methodology"
                isOptional
                hasContent={Boolean(background.treatmentPhilosophy)}
            >
                {(isEditing) => (
                    <Treatment
                        isEditing={isEditing}
                        initValue={background.treatmentPhilosophy}
                        conversationId={background.philosophyConversationId}
                        updateFn={updateBackgroundInfo}
                    />
                )}
            </FormCard>

            <FormCard
                type={hasContentForBiographic ? 'review' : 'add'}
                title="Demographic details"
                isOptional
                hasContent={hasContentForBiographic}
            >
                {(isEditing) => (
                    <DemographicDetails
                        isEditing={isEditing}
                        initValue={background.biographic}
                        hasContent={hasContentForBiographic}
                        updateFn={updateBackgroundInfo}
                    />
                )}
            </FormCard>

            <FormCard
                type={hasContentForRecognition ? 'review' : 'add'}
                title="Recognition"
                isOptional
                hasContent={hasContentForRecognition}
            >
                {(isEditing) => (
                    <Recognition
                        isEditing={isEditing}
                        awardRecognitionList={background.awardRecognitionList}
                        associationRecognitionList={background.associationRecognitionList}
                        publicationRecognitionList={background.publicationRecognitionList}
                        updateFn={updateBackgroundInfo}
                    />
                )}
            </FormCard>

            <FormCard
                type={!!licenseInfo?.malpracticeInsuranceCertificate?.licenseNumber ? 'review' : 'add'}
                title="Malpractice insurance certificate"
                isOptional
                hasContent={!!licenseInfo?.malpracticeInsuranceCertificate?.licenseNumber}
            >
                {(isEditing) => (
                    <MalpracticeInsuranceCertificate
                        isEditing={isEditing}
                        initValue={licenseInfo?.malpracticeInsuranceCertificate}
                        refetch={fetchLicenseData}
                        scheduleToken={scheduleToken}
                    />
                )}
            </FormCard>

            <FormCard
                type={!!licenseInfo?.deaLicense?.length ? 'review' : 'add'}
                title="DEA licenses"
                isOptional
                hasContent={!!licenseInfo?.deaLicense?.length}
            >
                {(isEditing) => (
                    <DEALicense
                        isEditing={isEditing}
                        initValue={licenseInfo?.deaLicense}
                        refetch={fetchLicenseData}
                        scheduleToken={scheduleToken}
                    />
                )}
            </FormCard>

            <FormCard
                type={!!licenseInfo?.collaboratingPhysician?.length ? 'review' : 'add'}
                title="Collaborating physician"
                isOptional
                hasContent={!!licenseInfo?.collaboratingPhysician?.length}
            >
                {(isEditing) => (
                    <CollaboratingPhysician
                        isEditing={isEditing}
                        initValue={licenseInfo?.collaboratingPhysician}
                        refresh={fetchLicenseData}
                        scheduleToken={scheduleToken}
                    />
                )}
            </FormCard>

            <FormCard
                type={hasAdditionalLicense ? 'review' : 'add'}
                title="Additional licences and certificates"
                isOptional
                hasContent={hasAdditionalLicense}
            >
                {(isEditing) => (
                    <AdditionalLicense
                        isEditing={isEditing}
                        initValue={licenseInfo?.additionalLicensesNCertificates}
                        refetch={fetchLicenseData}
                        scheduleToken={scheduleToken}
                    />
                )}
            </FormCard>
        </div>
    );
};

export default ProfileTab;
